<script>
import Chart from 'chart.js/auto';
import palette from '@inscribe/signal/dist/palette';
import { getPaletteColor } from './utils';
import { getDocumentTypeText } from '@/utils/documentType';
import { getFileTypeText } from '@/utils/fileType';

export default {
  components: {},
  props: {
    data: {
      type: Array,
      default: () => {},
    },
    size: {
      type: String,
      default: 'md',
    },
    chartPalette: {
      type: Array,
      default: () => [],
    },
    entity: {
      type: String,
      default: 'docs',
    },
    total: {
      type: Number,
      default: null,
    },
    brickOver: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return { palette };
  },
  watch: {
    data: function (newVal, oldVal) {
      if (this.chart) {
        this.chart.destroy();
        this.createChart();
      }
    },
    brickOver: function () {
      if (this.brickOver) {
        this.highlightSegment(this.brickOver.id);
      } else {
        this.restoreSegments();
      }
    },
  },
  mounted() {
    this.createChart();
  },
  methods: {
    getChartSegments() {
      return this.chart.getDatasetMeta(0).data.entries();
    },
    restoreSegments() {
      for (const [index, segment] of this.getChartSegments()) {
        segment.options.backgroundColor = getPaletteColor(
          this.data,
          index,
          this.chartPalette,
        );
      }
      this.chart.draw();
    },
    highlightSegment(highlightSegmentIndex) {
      for (const [index, segment] of this.getChartSegments()) {
        if (index !== highlightSegmentIndex) {
          segment.options.backgroundColor = palette['light-neutral-70'];
        }
      }
      this.chart.draw();
    },
    createChart() {
      const chartCanvas = this.$refs.doughnutChart;
      this.chart = new Chart(chartCanvas, {
        type: 'doughnut',
        data: {
          datasets: this.chartDataset(),
        },
        options: {
          cutout: '80%',
          plugins: {
            tooltip: {
              enabled: false,
              position: 'nearest',
              external: this.customTooltip,
            },
          },
        },
      });
    },

    chartDataset() {
      return [
        {
          data: this.data.map((d) => d.count),
          backgroundColor: this.data.map((d, idx) => {
            return getPaletteColor(this.data, idx, this.chartPalette);
          }),
        },
      ];
    },

    getOrCreateTooltip(chart) {
      let tooltipEl = chart.canvas.parentNode.querySelector('div.tooltip');
      if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.classList.add('tooltip');
        tooltipEl.style.background = 'rgba(0, 0, 0, 0.9)';
        tooltipEl.style.minWidth = '200px';
        tooltipEl.style.borderRadius = '12px';
        tooltipEl.style.padding = '12px';
        tooltipEl.style.color = 'white';
        tooltipEl.style.opacity = 1;
        tooltipEl.style.pointerEvents = 'none';
        tooltipEl.style.position = 'absolute';
        tooltipEl.style.transform = 'translate(-50%, 0)';
        tooltipEl.style.transition = 'all .1s ease';

        const table = document.createElement('table');
        table.style.margin = '0px';

        tooltipEl.appendChild(table);
        chart.canvas.parentNode.appendChild(tooltipEl);
      }

      return tooltipEl;
    },

    customTooltip(context) {
      // Tooltip Element
      const { chart, tooltip } = context;
      const tooltipEl = this.getOrCreateTooltip(chart);

      // Hide if no tooltip
      if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = 0;
        return;
      }
      const dataPoint = tooltip.dataPoints[0];
      let label = this.data[dataPoint.dataIndex].type;
      label =
        this.entity === 'docs'
          ? getDocumentTypeText(label)
          : getFileTypeText(label);

      tooltip.title = [label];
      const count = this.data[dataPoint.dataIndex].count;
      tooltip.body = [
        { lines: [`${count} ${count > 1 ? 'documents' : 'document'}`] },
      ];

      // Set Text
      if (tooltip.body) {
        const titleLines = tooltip.title || [];
        const bodyLines = tooltip.body.map((b) => b.lines);

        const tableHead = document.createElement('thead');

        titleLines.forEach((title, i) => {
          const colors = tooltip.labelColors[i];

          const tr = document.createElement('tr');
          tr.style.borderWidth = 0;

          const th = document.createElement('th');
          th.style.borderWidth = 0;
          th.style.borderLeft = `3px solid ${colors.backgroundColor}`;
          th.style.paddingLeft = '12px';

          const text = document.createTextNode(title);
          th.style.fontSize = `.75rem`;
          th.style.color = palette['dark-neutral-120'];

          th.appendChild(text);
          tr.appendChild(th);
          tableHead.appendChild(tr);
        });

        const tableBody = document.createElement('tbody');
        bodyLines.forEach((body, i) => {
          const colors = tooltip.labelColors[i];

          const tr = document.createElement('tr');
          tr.style.backgroundColor = 'inherit';
          tr.style.borderWidth = 0;

          const td = document.createElement('td');
          td.style.borderWidth = 0;
          td.style.borderLeft = `3px solid ${colors.backgroundColor}`;
          td.style.paddingLeft = '12px';
          td.style.fontSize = `.75rem`;
          td.style.color = palette['dark-neutral-110'];

          const text = document.createTextNode(body);

          td.appendChild(text);
          tr.appendChild(td);
          tableBody.appendChild(tr);
        });

        const tableRoot = tooltipEl.querySelector('table');

        // Remove old children
        while (tableRoot.firstChild) {
          tableRoot.firstChild.remove();
        }

        // Add new children
        tableRoot.appendChild(tableHead);
        tableRoot.appendChild(tableBody);
      }

      const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

      // Display, position, and set styles for font
      tooltipEl.style.opacity = 1;
      tooltipEl.style.left = positionX + tooltip.caretX + 'px';
      tooltipEl.style.top = positionY + tooltip.caretY + 'px';
      tooltipEl.style.font = tooltip.options.bodyFont.string;
      tooltipEl.style.padding = '12 px';
    },
  },
};
</script>

<template>
  <div
    :class="{ md: size === 'md', lg: size === 'lg' }"
    class="doughnut-chart d-flex flex-row align-items-center justify-content-center"
  >
    <div class="chart">
      <canvas ref="doughnutChart"></canvas>
      <div class="data-content d-flex flex-column align-items-center">
        <div class="entity i-text-subheading-3 i-text-neutral-110">
          {{ entity === 'docs' ? 'Docs' : 'Types' }}
        </div>
        <div class="docs i-text-heading-3 i-text-neutral-120">
          {{ total }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.doughnut-chart {
  &.md {
    canvas {
      width: 160px;
      height: 160px;

      @include respond-to('small') {
        width: 140px;
        height: 140px;
      }
    }
  }

  &.lg {
    canvas {
      width: 180px;
      height: 180px;

      @include respond-to('small') {
        width: 140px;
        height: 140px;
      }
    }
  }

  .chart {
    position: relative;
    .data-content {
      position: absolute;
      left: 50%;
      top: 50%;
      transform-origin: 0%;
      transform: translate(-50%, -50%);
    }
  }
}
</style>
